import React, { useState, useEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Switch,
  Typography,
  CircularProgress,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { FormDialogHeader, useFormDialogStyles } from "../FormDialog";
import {
  MESSAGE_FIELDS,
  CONFIRMATION_FIELDS,
  STATUS,
  estimateSeconds,
  formatDuration,
  paceSummary,
  previewMessage,
  parseCampaignSettings,
  isCampaignStopped,
} from "../../pages/Campaigns/campaignShared";

// Assistente de campanha em 4 passos: Básico, Público, Mensagem e Envio.
// Serve para criar e para editar (rascunho, programada ou pausada).

const STEPS = ["Básico", "Público", "Mensagem", "Envio"];
const BUILTIN_VARS = ["nome", "numero", "email"];

const useStyles = makeStyles((theme) => ({
  stepper: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    padding: theme.spacing(1.5, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#fbfaf7",
  },
  step: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 12px 4px 4px",
    borderRadius: 999,
    border: "1px solid transparent",
    background: "none",
    font: "inherit",
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    cursor: "pointer",
  },
  stepActive: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
  },
  stepDone: { color: theme.palette.type === "dark" ? "#81c784" : "#2e7d32" },
  stepNum: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#ece9e2",
  },
  stepNumActive: { backgroundColor: theme.palette.primary.main, color: "#fff" },
  stepNumDone: { backgroundColor: alpha("#2e7d32", 0.14) },
  body: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    alignItems: "flex-start",
  },
  main: { flex: "999 1 420px", minWidth: 0, display: "flex", flexDirection: "column", gap: theme.spacing(2) },
  side: { flex: "1 1 280px", minWidth: 0, display: "flex", flexDirection: "column", gap: theme.spacing(2) },
  stepTitle: { fontSize: 15, fontWeight: 600 },
  hint: { fontSize: 13, color: theme.palette.text.secondary, marginTop: 2 },
  choice: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    width: "100%",
    textAlign: "left",
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    font: "inherit",
    cursor: "pointer",
  },
  choiceActive: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  radioDot: {
    marginTop: 2,
    width: 18,
    height: 18,
    flexShrink: 0,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.text.secondary}`,
    boxSizing: "border-box",
  },
  radioDotActive: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 3px ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.primary.main,
  },
  choiceTitle: { fontSize: 14, fontWeight: 600 },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" },
  varChip: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
    minWidth: 0,
    height: 28,
    padding: theme.spacing(0, 1.25),
    borderRadius: 8,
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
  },
  variationChip: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    height: 30,
    minWidth: 0,
    borderRadius: 999,
    padding: theme.spacing(0, 1.5),
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
  },
  variationActive: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    borderColor: "transparent",
  },
  box: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
  },
  attach: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: theme.spacing(1.5),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 12,
  },
  attachIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#ece9e2",
  },
  sideLabel: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  chat: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#0b141a" : "#efe7dd",
  },
  bubbleOut: {
    alignSelf: "flex-end",
    maxWidth: "88%",
    padding: "8px 10px 6px",
    borderRadius: "10px 10px 2px 10px",
    fontSize: 14,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    backgroundColor: theme.palette.type === "dark" ? "#005c4b" : "#d9fdd3",
    boxShadow: "0 1px 1px rgba(0,0,0,.08)",
  },
  bubbleIn: {
    alignSelf: "flex-start",
    maxWidth: "70%",
    padding: "6px 10px",
    borderRadius: "10px 10px 10px 2px",
    fontSize: 14,
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    backgroundColor: theme.palette.type === "dark" ? "#202c33" : "#fff",
  },
  bubbleMeta: { textAlign: "right", fontSize: 11, opacity: 0.7, marginTop: 2 },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 13,
    "& > span:first-child": { color: theme.palette.text.secondary },
    "& > span:last-child": { fontWeight: 600, textAlign: "right", minWidth: 0, wordBreak: "break-word" },
  },
  okDot: { color: theme.palette.type === "dark" ? "#81c784" : "#2e7d32" },
  badDot: { color: theme.palette.error.main },
  warn: {
    fontSize: 13,
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    color: theme.palette.text.primary,
    backgroundColor: alpha("#b26a00", 0.1),
    border: `1px solid ${alpha("#b26a00", 0.3)}`,
  },
  info: {
    fontSize: 13,
    lineHeight: 1.5,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  footer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const emptyForm = () => ({
  name: "",
  whatsappId: "",
  audienceMode: "list",
  contactListId: "",
  tagId: "",
  messages: ["", "", "", "", ""],
  confirmation: false,
  confirmationMessages: ["", "", "", "", ""],
  fileListId: "",
  sendMode: "now", // now | later | draft
  scheduledAt: moment().add(1, "day").hour(9).minute(0).format("YYYY-MM-DDTHH:mm"),
});

const filled = (arr) => arr.filter((m) => m && m.trim()).length;

// Variações de texto (mensagem principal ou de confirmação)
const VariationEditor = ({ classes, values, onChange, label, variables, disabled, inputRef }) => {
  const [active, setActive] = useState(0);
  const visible = Math.max(1, ...values.map((v, i) => (v && v.trim() ? i + 1 : 0)), active + 1);

  useEffect(() => {
    if (active >= visible) setActive(0);
  }, [active, visible]);

  const insert = (token) => {
    const el = inputRef?.current;
    const text = values[active] || "";
    let next;
    if (el && typeof el.selectionStart === "number") {
      next = text.slice(0, el.selectionStart) + token + text.slice(el.selectionEnd);
    } else {
      next = text + token;
    }
    const copy = [...values];
    copy[active] = next;
    onChange(copy);
  };

  return (
    <>
      <div className={classes.chipRow}>
        {values.slice(0, visible).map((v, i) => (
          <Button
            key={i}
            variant="outlined"
            className={`${classes.variationChip} ${active === i ? classes.variationActive : ""}`}
            onClick={() => setActive(i)}
            aria-pressed={active === i}
          >
            Variação {i + 1}
          </Button>
        ))}
        {visible < 5 && !disabled && (
          <Button
            variant="outlined"
            className={classes.variationChip}
            style={{ borderStyle: "dashed" }}
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setActive(visible)}
          >
            Adicionar variação
          </Button>
        )}
        {active > 0 && !disabled && (
          <IconButton
            size="small"
            aria-label={`Remover variação ${active + 1}`}
            onClick={() => {
              const copy = [...values];
              copy.splice(active, 1);
              copy.push("");
              onChange(copy);
              setActive(active - 1);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <TextField
        label={`${label} — variação ${active + 1}`}
        variant="outlined"
        multiline
        minRows={6}
        fullWidth
        disabled={disabled}
        value={values[active] || ""}
        inputRef={inputRef}
        onChange={(e) => {
          const copy = [...values];
          copy[active] = e.target.value;
          onChange(copy);
        }}
      />
      {!disabled && (
        <div className={classes.chipRow}>
          <Typography variant="caption" color="textSecondary" style={{ marginRight: 4 }}>
            Inserir:
          </Typography>
          {variables.map((v) => (
            <Button key={v} variant="outlined" className={classes.varChip} onClick={() => insert(`{${v}}`)}>
              {`{${v}}`}
            </Button>
          ))}
          <Typography variant="caption" color="textSecondary" style={{ marginLeft: "auto" }}>
            {(values[active] || "").length} caracteres
          </Typography>
        </div>
      )}
    </>
  );
};

const CampaignModal = ({ open, onClose, campaignId, onSaved, initialContactListId }) => {
  const classes = useStyles();
  const fd = useFormDialogStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [record, setRecord] = useState(null); // campanha carregada (edição)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const [whatsapps, setWhatsapps] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [fileLists, setFileLists] = useState([]);
  const [settings, setSettings] = useState(parseCampaignSettings([]));
  const [audience, setAudience] = useState({ valid: null, invalid: null, sample: null });
  // Custo em créditos. null quando o plano não controla créditos (o padrão):
  // nada de crédito aparece na tela nesse caso.
  const [credit, setCredit] = useState(null);
  // Se a empresa pode disparar agora (em período de teste, não pode). null até
  // carregar. É só aviso: quem barra de fato é o backend.
  const [sending, setSending] = useState(null);
  const history = useHistory();

  const fileInput = useRef(null);
  const messageInput = useRef(null);
  const confirmationInput = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Dados de apoio
  useEffect(() => {
    if (!open) return;
    api.get("/whatsapp", { params: { session: 0 } }).then(({ data }) => setWhatsapps(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/contact-lists/list").then(({ data }) => setContactLists(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/tags").then(({ data }) => setTags((data.tags || data || []).map((t) => ({ id: t.id, name: t.name })))).catch(() => {});
    // Rota enxuta e sem paginação (id e nome de todas as listas).
    api.get("/files/list").then(({ data }) => setFileLists(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/campaign-settings").then(({ data }) => setSettings(parseCampaignSettings(data))).catch(() => {});
  }, [open]);

  // Campanha a editar
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setAttachment(null);
    if (!campaignId) {
      setRecord(null);
      setForm({ ...emptyForm(), contactListId: initialContactListId || "" });
      return;
    }
    setLoading(true);
    api
      .get(`/campaigns/${campaignId}`)
      .then(({ data }) => {
        setRecord(data);
        setForm({
          ...emptyForm(),
          name: data.name || "",
          whatsappId: data.whatsappId || "",
          contactListId: data.contactListId || "",
          messages: MESSAGE_FIELDS.map((f) => data[f] || ""),
          confirmation: !!data.confirmation,
          confirmationMessages: CONFIRMATION_FIELDS.map((f) => data[f] || ""),
          fileListId: data.fileListId || "",
          sendMode: data.scheduledAt ? "later" : isCampaignStopped(data.status) ? "later" : "draft",
          scheduledAt: data.scheduledAt
            ? moment(data.scheduledAt).format("YYYY-MM-DDTHH:mm")
            : emptyForm().scheduledAt,
        });
      })
      .catch((err) => {
        toastError(err);
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId]);

  // Números do público escolhido
  useEffect(() => {
    if (!open || form.audienceMode !== "list" || !form.contactListId) {
      setAudience({ valid: null, invalid: null, sample: null });
      return;
    }
    let alive = true;
    const listId = form.contactListId;
    Promise.all([
      api.get("/contact-list-items", { params: { contactListId: listId, valid: "true", pageNumber: 1 } }),
      api.get("/contact-list-items", { params: { contactListId: listId, valid: "false", pageNumber: 1 } }),
    ])
      .then(([v, inv]) => {
        if (!alive) return;
        setAudience({ valid: v.data.count, invalid: inv.data.count, sample: v.data.contacts?.[0] || null });
      })
      .catch(() => alive && setAudience({ valid: null, invalid: null, sample: null }));
    return () => {
      alive = false;
    };
  }, [open, form.audienceMode, form.contactListId]);

  // Créditos: recalcula quando muda o que mexe no custo — a lista, a mensagem
  // de confirmação (conta à parte) e a lista de arquivos (cada arquivo conta 1).
  // No público por tag a lista só existe depois de salvar; aí só o saldo é
  // mostrado e o backend confere o custo no salvamento.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const hasList = form.audienceMode === "list" && form.contactListId;
    const t = setTimeout(() => {
      const request = hasList
        ? api.get("/campaigns/credits/estimate", {
            params: {
              contactListId: form.contactListId,
              fileListId: form.fileListId || undefined,
              confirmation: Boolean(form.confirmation),
            },
          })
        : api.get("/campaigns/credits/balance").then(({ data }) => ({
            data: { balance: data, total: null },
          }));
      request
        .then(({ data }) => {
          if (!alive) return;
          setCredit(data && data.balance && data.balance.enabled ? data : null);
        })
        .catch(() => alive && setCredit(null));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [open, form.audienceMode, form.contactListId, form.fileListId, form.confirmation]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    api
      .get("/campaigns/sending-status")
      .then(({ data }) => alive && setSending(data))
      .catch(() => alive && setSending(null));
    return () => {
      alive = false;
    };
  }, [open]);

  const sendingBlocked = Boolean(sending && sending.allowed === false);

  // Em período de teste, o único destino possível é o rascunho: a escolha já
  // vem marcada, em vez de o usuário montar tudo e só ouvir o "não" no fim.
  useEffect(() => {
    if (sendingBlocked && form.sendMode !== "draft") set({ sendMode: "draft" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendingBlocked, form.sendMode]);

  const status = record?.status || "INATIVA";
  // Pausa manual (CANCELADA) e pausa por falta de crédito se comportam igual
  // aqui: dá para editar, e o envio continua pelo Retomar da lista.
  const isPaused = isCampaignStopped(status);
  const locked = record && !["INATIVA", "PROGRAMADA"].includes(status) && !isPaused;
  const creditShort =
    credit && credit.total !== null && !credit.sufficient
      ? credit.total - credit.balance.available
      : 0;

  const variables = useMemo(
    () => [...BUILTIN_VARS, ...(settings.variables || []).map((v) => v.key).filter(Boolean)],
    [settings]
  );

  const whatsapp = whatsapps.find((w) => String(w.id) === String(form.whatsappId));
  const contactList = contactLists.find((l) => String(l.id) === String(form.contactListId));
  const tag = tags.find((t) => String(t.id) === String(form.tagId));
  const firstMessage = form.messages.find((m) => m && m.trim()) || "";
  const firstConfirmation = form.confirmationMessages.find((m) => m && m.trim()) || "";
  const estimate = audience.valid ? estimateSeconds(audience.valid, settings) : null;

  // O que impede o envio (rascunho pode ser salvo com qualquer coisa faltando)
  const problems = [];
  if (!form.whatsappId) problems.push({ step: 0, text: "Escolha a conexão que vai enviar" });
  if (form.audienceMode === "list" && !form.contactListId) problems.push({ step: 1, text: "Escolha a lista de contatos" });
  if (form.audienceMode === "tag" && !form.tagId) problems.push({ step: 1, text: "Escolha a etiqueta" });
  if (!filled(form.messages)) problems.push({ step: 2, text: "Escreva a mensagem" });
  if (form.confirmation && !filled(form.confirmationMessages))
    problems.push({ step: 2, text: "Escreva a mensagem de confirmação" });
  if (form.sendMode === "later" && (!form.scheduledAt || moment(form.scheduledAt).isBefore(moment())))
    problems.push({ step: 3, text: "Escolha uma data e hora no futuro" });
  // Mesma regra do backend: agendar e enviar exigem saldo; rascunho não. Como é
  // um problema do passo de envio, o salvamento como rascunho continua livre
  // (save ignora problemas no modo "draft") e editar uma campanha pausada
  // também (o modo "keep" só considera os passos anteriores).
  if (creditShort > 0)
    problems.push({
      step: 3,
      text: `Faltam ${creditShort.toLocaleString("pt-BR")} créditos para enviar esta campanha`,
    });

  const nameOk = form.name.trim().length >= 3;

  const buildPayload = (mode) => {
    const payload = {
      name: form.name.trim(),
      whatsappId: form.whatsappId ? +form.whatsappId : null,
      contactListId: form.audienceMode === "list" && form.contactListId ? +form.contactListId : null,
      tagListId: form.audienceMode === "tag" && form.tagId ? +form.tagId : null,
      confirmation: form.confirmation,
      fileListId: form.fileListId ? +form.fileListId : null,
      status,
    };
    // mantém a lista atual quando o público por tag não foi trocado
    if (form.audienceMode === "tag" && !form.tagId && record?.contactListId) payload.contactListId = record.contactListId;
    // as variações são gravadas em ordem, sem buracos
    const msgs = form.messages.filter((m) => m && m.trim());
    MESSAGE_FIELDS.forEach((f, i) => {
      payload[f] = msgs[i] || null;
    });
    const confs = form.confirmationMessages.filter((m) => m && m.trim());
    CONFIRMATION_FIELDS.forEach((f, i) => {
      payload[f] = confs[i] || null;
    });
    if (mode === "later") payload.scheduledAt = moment(form.scheduledAt).format("YYYY-MM-DD HH:mm:ss");
    // fora da janela da verificação automática (1 h); o "enviar agora" logo
    // em seguida antecipa para já
    else if (mode === "now") payload.scheduledAt = moment().add(65, "minutes").format("YYYY-MM-DD HH:mm:ss");
    else if (mode === "keep") payload.scheduledAt = record?.scheduledAt ? moment(record.scheduledAt).format("YYYY-MM-DD HH:mm:ss") : null;
    else payload.scheduledAt = null;
    return payload;
  };

  const save = async (mode) => {
    if (!nameOk) {
      setStep(0);
      toast.error("Dê um nome à campanha (pelo menos 3 letras)");
      return;
    }
    const blocking = mode === "keep" ? problems.filter((p) => p.step < 3) : problems;
    if (mode !== "draft" && blocking.length) {
      setStep(blocking[0].step);
      toast.error(blocking[0].text);
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(mode);
      const { data } = record
        ? await api.put(`/campaigns/${record.id}`, payload)
        : await api.post("/campaigns", payload);

      if (attachment) {
        const formData = new FormData();
        formData.append("file", attachment);
        await api.post(`/campaigns/${data.id}/media-upload`, formData);
      }

      if (mode === "now") {
        try {
          await api.post(`/campaigns/${data.id}/send-now`);
        } catch (e) {
          // a verificação automática pode ter começado o envio antes; tudo bem
        }
      }

      const msg = {
        now: "Envio iniciado",
        later: `Campanha agendada para ${moment(form.scheduledAt).format("DD/MM [às] HH:mm")}`,
        draft: "Rascunho salvo",
        keep: "Alterações salvas",
      }[mode];
      toast.success(msg);

      let saved = data;
      try {
        saved = (await api.get(`/campaigns/${data.id}`)).data;
      } catch (e) {
        // usa o que voltou do salvamento
      }
      if (onSaved) onSaved(saved);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const removeMedia = async () => {
    if (attachment) {
      setAttachment(null);
      if (fileInput.current) fileInput.current.value = null;
      return;
    }
    if (record?.mediaPath) {
      try {
        await api.delete(`/campaigns/${record.id}/media-upload`);
        setRecord((r) => ({ ...r, mediaPath: null, mediaName: null }));
        toast.success("Anexo removido");
      } catch (err) {
        toastError(err);
      }
    }
  };

  const mediaName = attachment ? attachment.name : record?.mediaName;

  // ─── Passos ───────────────────────────────────────────────────────────────
  const stepBasic = (
    <>
      <div>
        <div className={classes.stepTitle}>Básico</div>
        <div className={classes.hint}>Um nome para você reconhecer a campanha e o número que vai enviar.</div>
      </div>
      <TextField
        label="Nome da campanha"
        variant="outlined"
        fullWidth
        autoFocus
        disabled={locked}
        value={form.name}
        onChange={(e) => set({ name: e.target.value })}
        error={form.name.length > 0 && !nameOk}
        helperText={form.name.length > 0 && !nameOk ? "Use pelo menos 3 letras" : "Ex.: Black Friday — clientes ativos"}
        inputProps={{ maxLength: 120 }}
      />
      <TextField
        select
        label="Conexão (número que envia)"
        variant="outlined"
        fullWidth
        disabled={locked}
        value={form.whatsappId}
        onChange={(e) => set({ whatsappId: e.target.value })}
      >
        {whatsapps.map((w) => (
          <MenuItem key={w.id} value={w.id}>
            {w.name}
            <span
              className={w.status === "CONNECTED" ? classes.okDot : classes.badDot}
              style={{ marginLeft: 8, fontSize: 12 }}
            >
              ● {w.status === "CONNECTED" ? "conectada" : "desconectada"}
            </span>
          </MenuItem>
        ))}
      </TextField>
      {whatsapp && whatsapp.status !== "CONNECTED" && (
        <div className={classes.warn}>
          Esta conexão está desconectada agora. As mensagens só saem com ela conectada.
        </div>
      )}
    </>
  );

  const choice = (active, title, text, onClick) => (
    <button type="button" className={`${classes.choice} ${active ? classes.choiceActive : ""}`} onClick={onClick} disabled={locked} aria-pressed={active}>
      <span className={`${classes.radioDot} ${active ? classes.radioDotActive : ""}`} />
      <span>
        <span className={classes.choiceTitle}>{title}</span>
        <span className={classes.hint} style={{ display: "block" }}>{text}</span>
      </span>
    </button>
  );

  const stepAudience = (
    <>
      <div>
        <div className={classes.stepTitle}>Público</div>
        <div className={classes.hint}>Quem vai receber. Só recebem os contatos que têm WhatsApp.</div>
      </div>
      {choice(form.audienceMode === "list", "Lista de contatos", "Uma lista importada de planilha ou montada à mão.", () => set({ audienceMode: "list" }))}
      {form.audienceMode === "list" && (
        <TextField
          select
          label="Lista"
          variant="outlined"
          fullWidth
          disabled={locked}
          value={form.contactListId}
          onChange={(e) => set({ contactListId: e.target.value })}
          helperText={contactLists.length === 0 ? "Nenhuma lista ainda — crie em Campanhas › Listas de Contatos" : " "}
        >
          {contactLists.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {choice(form.audienceMode === "tag", "Contatos com uma etiqueta", "Uma lista nova é criada com os contatos dos atendimentos que têm a etiqueta.", () => set({ audienceMode: "tag" }))}
      {form.audienceMode === "tag" && (
        <TextField
          select
          label="Etiqueta"
          variant="outlined"
          fullWidth
          disabled={locked}
          value={form.tagId}
          onChange={(e) => set({ tagId: e.target.value })}
          helperText={record?.contactListId ? "Deixe em branco para manter a lista atual" : " "}
        >
          {tags.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {form.audienceMode === "list" && audience.valid !== null && (
        <div className={classes.info}>
          <b>{audience.valid}</b> {audience.valid === 1 ? "contato com WhatsApp vai" : "contatos com WhatsApp vão"} receber
          {audience.invalid > 0 && <> · {audience.invalid} sem WhatsApp ficam de fora</>}.
        </div>
      )}
    </>
  );

  const stepMessage = (
    <>
      <div>
        <div className={classes.stepTitle}>Mensagem</div>
        <div className={classes.hint}>
          Crie até 5 variações. Cada contato recebe uma delas, sorteada — isso ajuda a evitar bloqueios.
        </div>
      </div>
      <VariationEditor
        classes={classes}
        label="Mensagem"
        values={form.messages}
        onChange={(messages) => set({ messages })}
        variables={variables}
        disabled={locked}
        inputRef={messageInput}
      />

      <div className={classes.attach}>
        <div className={classes.attachIcon}>
          <ImageOutlinedIcon />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.choiceTitle}>{mediaName || "Anexo (opcional)"}</div>
          <div className={classes.hint}>
            {mediaName ? "Vai junto com a mensagem, como legenda." : "Imagem, vídeo ou PDF enviado junto com a mensagem."}
          </div>
        </div>
        <input ref={fileInput} type="file" hidden onChange={(e) => e.target.files?.[0] && setAttachment(e.target.files[0])} />
        {mediaName ? (
          <IconButton aria-label="Remover anexo" onClick={removeMedia} disabled={locked}>
            <DeleteOutlineIcon />
          </IconButton>
        ) : (
          <Button variant="outlined" className={fd.secondaryButton} disabled={locked} onClick={() => fileInput.current?.click()} style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}>
            Escolher arquivo
          </Button>
        )}
      </div>

      {fileLists.length > 0 && (
        <TextField
          select
          label="Lista de arquivos (opcional)"
          variant="outlined"
          fullWidth
          disabled={locked}
          value={form.fileListId}
          onChange={(e) => set({ fileListId: e.target.value })}
          helperText="Os arquivos da lista são enviados antes da mensagem."
        >
          <MenuItem value="">Nenhuma</MenuItem>
          {fileLists.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <div className={classes.box}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div className={classes.choiceTitle}>Pedir confirmação antes</div>
            <div className={classes.hint}>
              O contato recebe primeiro uma pergunta. Só quem responder recebe a mensagem acima.
            </div>
          </div>
          <Switch
            color="primary"
            checked={form.confirmation}
            disabled={locked}
            onChange={(e) => set({ confirmation: e.target.checked })}
            inputProps={{ "aria-label": "Pedir confirmação antes" }}
          />
        </div>
        {form.confirmation && (
          <VariationEditor
            classes={classes}
            label="Pergunta de confirmação"
            values={form.confirmationMessages}
            onChange={(confirmationMessages) => set({ confirmationMessages })}
            variables={variables}
            disabled={locked}
            inputRef={confirmationInput}
          />
        )}
      </div>
    </>
  );

  const sendChoice = (key, title, text) =>
    choice(form.sendMode === key, title, text, () => set({ sendMode: key }));

  const stepSend = (
    <>
      <div>
        <div className={classes.stepTitle}>Envio</div>
        <div className={classes.hint}>Ritmo atual: {paceSummary(settings)}. Dá para mudar em Campanhas › Configurações.</div>
      </div>
      {isPaused ? (
        <div className={classes.info}>
          {status === "PAUSADA_SEM_CREDITO" ? (
            <>
              O envio desta campanha parou porque os créditos acabaram. Depois da recarga, use <b>Retomar</b> na lista —
              quem já recebeu não recebe de novo nem é cobrado outra vez.
            </>
          ) : (
            <>
              Esta campanha está pausada. Salve as alterações e use <b>Retomar</b> na lista para continuar o envio — quem
              já recebeu não recebe de novo.
            </>
          )}
        </div>
      ) : (
        <>
          {sendingBlocked && (
            <div className={classes.info}>
              {sending.message}{" "}
              <Button
                size="small"
                color="primary"
                style={{ textTransform: "none", padding: "0 4px", minWidth: 0 }}
                onClick={() => {
                  onClose();
                  history.push("/subscription");
                }}
              >
                Ver assinatura
              </Button>
            </div>
          )}
          {!sendingBlocked && sendChoice("now", "Enviar agora", "Começa em alguns segundos.")}
          {!sendingBlocked && sendChoice("later", "Agendar", "Escolha o dia e a hora do início.")}
          {form.sendMode === "later" && (
            <TextField
              type="datetime-local"
              label="Começar em"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={form.scheduledAt}
              onChange={(e) => set({ scheduledAt: e.target.value })}
              inputProps={{ min: moment().format("YYYY-MM-DDTHH:mm") }}
              style={{ maxWidth: 280 }}
            />
          )}
          {sendChoice("draft", "Salvar como rascunho", "Fica guardada sem data, para enviar depois.")}
        </>
      )}
      {credit && !isPaused && form.sendMode !== "draft" && (
        <div className={creditShort > 0 ? classes.warn : classes.info}>
          {credit.total === null ? (
            <>
              {credit.balance.available.toLocaleString("pt-BR")} créditos disponíveis. No público por etiqueta, o custo é
              conferido ao salvar, quando a lista com os contatos da tag é montada.
            </>
          ) : (
            <>
              Esta campanha vai usar <b>{credit.total.toLocaleString("pt-BR")} créditos</b> (
              {credit.validContacts.toLocaleString("pt-BR")} contatos × {credit.messagesPerContact}{" "}
              {credit.messagesPerContact === 1 ? "mensagem" : "mensagens"}). Disponível:{" "}
              <b>{credit.balance.available.toLocaleString("pt-BR")}</b>.
              {creditShort > 0 && (
                <>
                  {" "}
                  Faltam {creditShort.toLocaleString("pt-BR")}: salve como rascunho e agende depois da recarga.
                </>
              )}
            </>
          )}
        </div>
      )}
      {problems.filter((p) => p.step < 3).length > 0 && form.sendMode !== "draft" && !isPaused && (
        <div className={classes.warn}>
          Antes de enviar:{" "}
          {problems
            .filter((p) => p.step < 3)
            .map((p, i) => (
              <span key={p.text}>
                {i > 0 && " · "}
                <Button size="small" color="primary" style={{ textTransform: "none", padding: "0 4px", minWidth: 0 }} onClick={() => setStep(p.step)}>
                  {p.text}
                </Button>
              </span>
            ))}
        </div>
      )}
    </>
  );

  // ─── Lateral: prévia e resumo ─────────────────────────────────────────────
  const sample = audience.sample;
  const side = (
    <div className={classes.side}>
      <div className={classes.sideLabel}>Como o contato vai ver</div>
      <div className={classes.chat}>
        {form.confirmation && (
          <>
            <div className={classes.bubbleOut}>
              {previewMessage(firstConfirmation, sample, settings.variables) || "Pergunta de confirmação…"}
              <div className={classes.bubbleMeta}>14:02</div>
            </div>
            <div className={classes.bubbleIn}>Sim</div>
          </>
        )}
        <div className={classes.bubbleOut}>
          {(attachment || record?.mediaPath) && (
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Anexo: {mediaName}</div>
          )}
          {previewMessage(firstMessage, sample, settings.variables) || "Sua mensagem aparece aqui…"}
          <div className={classes.bubbleMeta}>14:02 ✓✓</div>
        </div>
      </div>
      <Typography variant="caption" color="textSecondary">
        {sample ? `Exemplo com o 1º contato da lista (${sample.name}).` : "Exemplo com um contato fictício."}
      </Typography>

      <div className={classes.box}>
        <div className={classes.sideLabel}>Resumo</div>
        <div className={classes.summaryRow}>
          <span>Nome</span>
          <span>{form.name || "—"}</span>
        </div>
        <div className={classes.summaryRow}>
          <span>Conexão</span>
          <span>
            {whatsapp ? whatsapp.name : "—"}
            {whatsapp && (
              <span className={whatsapp.status === "CONNECTED" ? classes.okDot : classes.badDot} style={{ fontWeight: 400 }}>
                {" "}● {whatsapp.status === "CONNECTED" ? "conectada" : "desconectada"}
              </span>
            )}
          </span>
        </div>
        <div className={classes.summaryRow}>
          <span>Público</span>
          <span>
            {form.audienceMode === "tag"
              ? tag
                ? `Etiqueta: ${tag.name}`
                : record?.contactList?.name || "—"
              : contactList?.name || "—"}
          </span>
        </div>
        {audience.valid !== null && (
          <div className={classes.summaryRow}>
            <span>Contatos</span>
            <span>
              {audience.valid} com WhatsApp
              {audience.invalid > 0 && <span style={{ fontWeight: 400 }}> ({audience.invalid} sem)</span>}
            </span>
          </div>
        )}
        {estimate !== null && (
          <div className={classes.summaryRow}>
            <span>Duração estimada</span>
            <span>~ {formatDuration(estimate)}</span>
          </div>
        )}
        {credit && credit.total !== null && (
          <div className={classes.summaryRow}>
            <span>Créditos</span>
            <span className={creditShort > 0 ? classes.badDot : undefined}>
              {credit.total.toLocaleString("pt-BR")}
              <span style={{ fontWeight: 400 }}> de {credit.balance.available.toLocaleString("pt-BR")} disponíveis</span>
            </span>
          </div>
        )}
        {filled(form.messages) > 0 && (
          <div className={classes.summaryRow}>
            <span>Variações</span>
            <span>{filled(form.messages)}</span>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Rodapé ───────────────────────────────────────────────────────────────
  const finalAction = isPaused
    ? { label: "Salvar alterações", mode: "keep" }
    : { now: { label: "Iniciar envio", mode: "now" }, later: { label: "Agendar envio", mode: "later" }, draft: { label: "Salvar rascunho", mode: "draft" } }[form.sendMode];

  const title = record ? (locked ? "Campanha" : "Editar campanha") : "Nova campanha";
  const subtitle = locked
    ? `Esta campanha está ${STATUS[status]?.label.toLowerCase()} e não pode mais ser alterada.`
    : `Passo ${step + 1} de 4${!isPaused && status !== "PROGRAMADA" ? " · dá para salvar como rascunho a qualquer momento" : ""}`;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      classes={{ paper: fd.paper }}
      aria-labelledby="campaign-dialog-title"
    >
      <FormDialogHeader id="campaign-dialog-title" title={title} subtitle={subtitle} onClose={onClose} disabled={saving} />

      <nav className={classes.stepper} aria-label="Passos">
        {STEPS.map((label, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <button
              key={label}
              type="button"
              className={`${classes.step} ${active ? classes.stepActive : ""} ${done ? classes.stepDone : ""}`}
              onClick={() => (nameOk || i === 0 ? setStep(i) : toast.error("Dê um nome à campanha primeiro"))}
              aria-current={active ? "step" : undefined}
            >
              <span className={`${classes.stepNum} ${active ? classes.stepNumActive : ""} ${done ? classes.stepNumDone : ""}`}>
                {done ? <CheckIcon style={{ fontSize: 14 }} /> : i + 1}
              </span>
              {label}
            </button>
          );
        })}
      </nav>

      <DialogContent style={{ padding: 0 }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <div className={classes.body}>
            <div className={classes.main}>
              {step === 0 && stepBasic}
              {step === 1 && stepAudience}
              {step === 2 && stepMessage}
              {step === 3 && stepSend}
            </div>
            {side}
          </div>
        )}
      </DialogContent>

      <DialogActions className={classes.footer} style={{ margin: 0 }}>
        <Button
          variant="outlined"
          className={`${fd.actionButton} ${fd.secondaryButton}`}
          disabled={saving}
          onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
        >
          {step === 0 ? "Cancelar" : "Voltar"}
        </Button>
        {!locked && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {step < 3 && status === "INATIVA" && (
              <Button className={`${fd.actionButton} ${fd.cancelButton}`} disabled={saving} onClick={() => save("draft")}>
                Salvar rascunho
              </Button>
            )}
            {step < 3 && (status === "PROGRAMADA" || isPaused) && (
              <Button className={`${fd.actionButton} ${fd.cancelButton}`} disabled={saving} onClick={() => save("keep")}>
                Salvar alterações
              </Button>
            )}
            {step < 3 ? (
              <Button
                variant="contained"
                color="primary"
                disableElevation
                className={fd.actionButton}
                disabled={saving}
                onClick={() => (nameOk ? setStep(step + 1) : toast.error("Dê um nome à campanha (pelo menos 3 letras)"))}
              >
                Próximo: {STEPS[step + 1]}
              </Button>
            ) : (
              <div className={fd.progressWrapper}>
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  className={fd.actionButton}
                  disabled={saving}
                  onClick={() => save(finalAction.mode)}
                >
                  {finalAction.label}
                </Button>
                {saving && <CircularProgress size={24} className={fd.buttonProgress} />}
              </div>
            )}
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CampaignModal;
